import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, ArrowRight } from "lucide-react";
import { TryadLogo } from "@/components/TryadLogo";

export function NameDialog() {
  const { state, nameSheetOpen, setNameSheetOpen, saveName } = useApp();
  const [name, setName] = useState("");

  const isOnboarding = nameSheetOpen === "onboarding";
  const isOpen = nameSheetOpen !== null;

  useEffect(() => {
    if (isOpen) {
      setName(state?.userName || "");
    }
  }, [isOpen, state?.userName]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    saveName(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isOnboarding) {
          setNameSheetOpen(null);
        }
      }}
    >
      <DialogContent
        className="glass-surface rounded-3xl border border-primary/20 max-w-sm p-0 overflow-hidden [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => { if (isOnboarding) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isOnboarding) e.preventDefault(); }}
      >
        <div className="p-7 flex flex-col items-center text-center">
          {/* Icon */}
          <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mb-5">
            {isOnboarding ? (
              <TryadLogo size={22} className="text-primary" />
            ) : (
              <Pencil className="h-5 w-5 text-primary" />
            )}
          </div>

          <DialogHeader className="text-center space-y-1.5">
            <DialogTitle className="text-foreground text-xl font-bold tracking-tight">
              {isOnboarding ? "¡Bienvenido a TRYAD!" : "Editar nombre"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
              {isOnboarding
                ? "¿Cómo te llamas? Lo usaremos para saludarte cada día."
                : "Elige cómo quieres que te llamemos."}
            </DialogDescription>
          </DialogHeader>

          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Tu nombre"
            maxLength={30}
            autoFocus
            className="w-full h-12 mt-5 rounded-xl bg-secondary/30 border-border/40 text-foreground font-semibold text-center text-base focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
          />

          <div className="flex gap-2.5 w-full mt-5">
            {!isOnboarding && (
              <Button
                variant="ghost"
                onClick={() => setNameSheetOpen(null)}
                className="flex-1 h-12 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60 font-semibold"
              >
                Cancelar
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!name.trim()}
              className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-bold gap-2 hover:bg-primary/90 disabled:opacity-40 transition-colors duration-200"
            >
              <ArrowRight className="h-4 w-4" />
              {isOnboarding ? "Empezar" : "Guardar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
